/**
 * LeaveAppDialog.tsx — in-app "Leave OptiYield?" warning (T-071)
 *
 * Shown when the browser Back button reaches the app-exit boundary, intercepted by
 * useMapUrlState (T-237, which subsumed the original T-071 back-navigation guard). Reuses the
 * SettingsDialog idiom (Radix Dialog + shared Button variants) so it matches the app's
 * modal styling and inherits focus-trap / background-inert behaviour — NOT a native
 * window.confirm. Dismissing the dialog (Escape / overlay / X) is treated as "Stay".
 */
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'

interface LeaveAppDialogProps {
  open: boolean
  /** Dismiss and stay in the app (also the route for Escape / overlay dismiss). */
  onStay: () => void
  /** Confirm leaving — navigates back out of the SPA. */
  onLeave: () => void
}

export function LeaveAppDialog({ open, onStay, onLeave }: LeaveAppDialogProps) {
  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        // Any dismiss (Escape, overlay click, close X) means "Stay".
        if (!next) onStay()
      }}
    >
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>Leave OptiYield?</DialogTitle>
          <DialogDescription>Your current view will be lost.</DialogDescription>
        </DialogHeader>
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={onStay}>
            Stay
          </Button>
          <Button variant="destructive" onClick={onLeave}>
            Leave
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}
